import { LoaderFunction, redirect, useLoaderData } from "react-router-dom";

import { get, pget } from "@/api";
import { ClaimReferralRewardContainer } from "@/modules/referralRewards";
import { getSignupRewardInclusions } from "@/modules/rewards/rewards.utils";
import { loadIfFlag } from "@/services/Flags";
import { waitObj } from "@/toolbox/Promise";

import { queryClient } from "../../Root";

const getReferringPerson = pget(
  "/referral-codes/anonymous/stateless-share-url/{prettyShareToken}"
);
const getPublicListings = pget("/listings/public");

const load = async (request: Request, referralCode: string) => {
  const locationId = new URL(request.url).searchParams.get("locationId");

  const platformLocationId = locationId;

  const platform = await queryClient.fetchQuery(["/platform/current"], () =>
    get("/platform/current").catch(() => null)
  );

  const user = await queryClient.fetchQuery(["/user"], () =>
    get("/user").catch(() => null)
  );

  const referringPerson = queryClient.fetchQuery(
    [
      getReferringPerson.keyed({ prettyShareToken: referralCode }),
      referralCode,
    ],
    () => getReferringPerson({ prettyShareToken: referralCode })
  );

  const activeSignupReward = queryClient
    .fetchQuery(
      ["/incentives/{platformLocationId}/active-signup-reward/anonymous"],
      () =>
        get("/incentives/{platformLocationId}/active-signup-reward/anonymous", {
          platformLocationId: platformLocationId ?? "",
        })
    )
    .catch(() => null);

  const listings = await queryClient
    .fetchQuery(getPublicListings.key(), () => getPublicListings())
    .catch(() => []);

  // Get top 4 listings by sell volume
  const topListings = [...(listings || [])]
    .sort((a, b) => (b.sellVolume || 0) - (a.sellVolume || 0))
    .slice(0, 5);

  const activeSignupRewardData = await activeSignupReward;

  const signupRewardInclusions = getSignupRewardInclusions(
    activeSignupRewardData,
    listings
  );

  return waitObj({
    platform,
    referralCode,
    topListings,
    referringPerson,
    activeSignupReward: activeSignupRewardData,
    platformLocationId,
    signupRewardInclusions,
    user,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const claimReferralRewardLoader: LoaderFunction = loadIfFlag(
  "referral-rewards-phase-2"
)(async ({ params: { referralCode, user }, request }) => {
  if (!referralCode) {
    return redirect("/signin");
  }

  const result = await load(request, referralCode);

  if (result.user) {
    return redirect("/");
  }

  return result;
});

export default function ClaimReferralRewardPage() {
  const {
    platform,
    referralCode,
    referringPerson,
    activeSignupReward,
    platformLocationId,
    topListings,
    signupRewardInclusions,
  } = useLoaderData() as LoaderData;

  return (
    <ClaimReferralRewardContainer
      platform={platform!}
      referralCode={referralCode!}
      referringPerson={referringPerson!}
      activeSignupReward={activeSignupReward!}
      platformLocationId={platformLocationId}
      topListings={topListings}
      signupRewardInclusions={signupRewardInclusions}
    />
  );
}
